import { roundOre } from './money.js';
/**
 * Annuity (annuitetslån) amortization.
 * Level total payment: M = P·r·(1+r)^n / ((1+r)^n − 1)
 *   P = principal, r = periodic (monthly) rate, n = number of payments.
 * Reference: finance-app/danish-personal-finance-rules §2.
 */
export function annuityPayment(principal, periodicRate, periods) {
    if (periods <= 0)
        throw new Error('periods must be > 0');
    if (periodicRate === 0)
        return roundOre(principal / periods);
    const f = Math.pow(1 + periodicRate, periods);
    return roundOre((principal * periodicRate * f) / (f - 1));
}
/**
 * Generate a full amortization schedule. Each period is rounded to whole øre;
 * the final payment absorbs any rounding residual so the balance closes to exactly 0.
 * The interest slice is consumptive; the principal slice is liability-neutral (see net-worth taxonomy).
 */
export function amortizationSchedule(principal, periodicRate, periods) {
    const level = annuityPayment(principal, periodicRate, periods);
    const rows = [];
    let balance = principal;
    for (let p = 1; p <= periods; p++) {
        const interest = roundOre(balance * periodicRate);
        let payment = level;
        let principalPortion = payment - interest;
        // Last period (or overshoot): settle the exact remaining balance.
        if (p === periods || principalPortion >= balance) {
            principalPortion = balance;
            payment = principalPortion + interest;
        }
        const closing = balance - principalPortion;
        rows.push({
            period: p,
            openingBalance: balance,
            payment,
            interest,
            principal: principalPortion,
            closingBalance: closing,
        });
        balance = closing;
        if (balance <= 0)
            break;
    }
    return rows;
}
/**
 * Serial loan (serielån): a constant principal slice each period; the interest — and
 * therefore the total payment — falls over the life of the loan. Principal per period =
 * principal / periods, rounded to whole øre with the residual settled in the final period.
 * The interest slice is consumptive; the principal slice is liability-neutral.
 */
export function serialSchedule(principal, periodicRate, periods) {
    if (periods <= 0)
        throw new Error('periods must be > 0');
    const slice = roundOre(principal / periods);
    const rows = [];
    let balance = principal;
    for (let p = 1; p <= periods; p++) {
        const interest = roundOre(balance * periodicRate);
        let principalPortion = slice;
        if (p === periods || principalPortion >= balance)
            principalPortion = balance;
        const payment = principalPortion + interest;
        const closing = balance - principalPortion;
        rows.push({ period: p, openingBalance: balance, payment, interest, principal: principalPortion, closingBalance: closing });
        balance = closing;
        if (balance <= 0)
            break;
    }
    return rows;
}
/**
 * Interest-only loan (afdragsfrit lån): pay only the interest each period; the full
 * principal falls due as a bullet repayment in the final period. Every interest payment
 * is consumptive; the closing bullet is the liability-neutral principal repayment.
 */
export function interestOnlySchedule(principal, periodicRate, periods) {
    if (periods <= 0)
        throw new Error('periods must be > 0');
    const rows = [];
    const interest = roundOre(principal * periodicRate);
    for (let p = 1; p <= periods; p++) {
        const isLast = p === periods;
        const principalPortion = isLast ? principal : 0;
        rows.push({
            period: p,
            openingBalance: principal,
            payment: interest + principalPortion,
            interest,
            principal: principalPortion,
            closingBalance: principal - principalPortion,
        });
    }
    return rows;
}
/**
 * Dispatch to the right amortization shape for a formal loan. Informal loans are a
 * manual running-balance ledger and have no computed schedule.
 */
export function loanSchedule(type, principal, periodicRate, periods) {
    switch (type) {
        case 'serial':
            return serialSchedule(principal, periodicRate, periods);
        case 'interest_only':
            return interestOnlySchedule(principal, periodicRate, periods);
        case 'annuity':
            return amortizationSchedule(principal, periodicRate, periods);
    }
}
/** Total interest paid over the life of the loan (the consumptive slice). */
export function totalInterest(schedule) {
    return schedule.reduce((sum, r) => sum + r.interest, 0);
}
/** Total principal repaid over the schedule (the liability-neutral slice). */
export function totalPrincipal(schedule) {
    return schedule.reduce((sum, r) => sum + r.principal, 0);
}
//# sourceMappingURL=annuity.js.map